// productName.js — split a bilingual QuickBooks item name into English / Korean / pack size.
//
// QB names arrive in a handful of shapes (Brian types them in QB; the daily CSV carries them):
//   "PMO) Soy Sauce Bibimbap (간장 비빔밥) 20x270g"             English + (Korean) + pack
//   "HT) 참미역 12x5oz"                                          Korean only
//   "HT) 천일염 (Solar Seasalt) 12x3LB"                          Korean + (English)
//   "PMO) 짬뽕 16x390g..PMO) Fresh Noodle Jjamppong 16x390g"      two full names joined by ".."
//   "PMO) 매콤 쌈장 (튜브형) 12.7oz X 12"                         weight-first pack
//   "동원) 보성홍차 아이스티 제로 레몬 500ML ( 24입 )"              count in parentheses
//   "PMO) 자연은 맛있다 (mild) 8x(4x104g)"                        inner pack
//   "HT) Daipoong Rice (Premium) 15LB"                            single unit, weight only
//
// Nothing here is authoritative: the presentation sheet lets Brian overwrite every derived
// value by hand. This only gets the first draft right for most items (2026-11-04: pack size
// parses for ~250 of 272 active names; 166 are bilingual, 104 Korean-only, 2 English-only).

const HANGUL = /[가-힣ㄱ-ㆎ]/;
const LATIN = /[A-Za-z]{2,}/;
// Weight / count units that may follow a number. "fl oz" before "l" so it wins the alternation.
const UNIT = "(?:fl\\s*oz|kg|g|oz|lbs?|ml|l|ea|pcs?|p|장|개|입|팩|pack)";

export function hasHangul(s) { return HANGUL.test(s || ""); }
function hasLatin(s) { return LATIN.test(s || ""); }
function tidy(s) {
  return (s || "")
    .replace(/\s+/g, " ")
    .replace(/^[\s\-–·,]+/, "")
    .replace(/[\s\-–·,]+$/, "")
    .trim();
}
function tidyPack(n, w) { return `${n} x ${tidy(w).replace(/\s*([xX×])\s*/g, "$1")}`; }

// Pull the pack size off the END of a name segment. Returns { rest, units, pack }.
// units = cases-to-units count (what Price/Unit divides by); pack = the printable "20 x 270g".
export function splitPack(seg) {
  let s = (seg || "").replace(/\.+\s*$/, "").trim();
  let m;
  // A trailing "(6 pack)" / "(12 pcs)" describes the unit, not the case — keep it on the pack text.
  let suffix = "";
  m = s.match(/^(.*?)\s*\(([^()]*(?:pack|pcs?|ea|입|개)[^()]*)\)\s*$/i);
  if (m && !hasHangul(m[2])) { s = m[1].trim(); suffix = ` (${tidy(m[2])})`; }
  // "20x270g", "8x(4x104g)", "30x20장", "10 x 4LB", "6x5P" — any letters may follow the weight
  // ("20z" typo, "5P"); "cm"/"mm" is a dimension (kitchenware), not a pack.
  m = s.match(/^(.*?)[\s,]*(\d+)\s*[xX×*]\s*(\(?[\d.,]+\s*[A-Za-zㄱ-ㆎ가-힣]*(?:\s*[xX×]\s*[\d.,]+\s*[A-Za-z]*)?\)?)\s*$/);
  if (m && !/\d\s*(?:cm|mm|inch|in)\b/i.test(m[3])) return { rest: tidy(m[1]), units: parseInt(m[2], 10), pack: tidyPack(m[2], m[3]) + suffix };
  // "12.7oz X 12", "140gX20", "(297g) x 8", "7.3 FL OZ X 12", "(12x140g) x 4 pack"
  m = s.match(new RegExp("^(.*?)[\\s,]*\\(?((?:[\\d.,]+\\s*[xX×]\\s*)?[\\d.,]+\\s*" + UNIT + ")\\)?\\s*[xX×*]\\s*(\\d+)\\s*(?:pack|pcs?|ea|cs|입|개)?\\s*$", "i"));
  if (m) return { rest: tidy(m[1]), units: parseInt(m[3], 10), pack: tidyPack(m[3], m[2]) + suffix };
  // "500ML ( 24입 )", "( 24입 )", "(12 ea)"
  m = s.match(new RegExp("^(.*?)[\\s,]*(?:([\\d.,]+\\s*" + UNIT + ")\\s*)?\\(\\s*(\\d+)\\s*(?:입|ea|pcs?|개)\\s*\\)\\s*$", "i"));
  if (m) return { rest: tidy(m[1]), units: parseInt(m[3], 10), pack: m[2] ? tidyPack(m[3], m[2]) : `${m[3]} ea` };
  // "15LB" alone — one unit, weight only (Price/Unit = case price; units stays unknown)
  m = s.match(new RegExp("^(.*?)[\\s,]+([\\d.,]+\\s*" + UNIT + ")\\s*$", "i"));
  if (m) return { rest: tidy(m[1]), units: null, pack: tidy(m[2]) };
  return { rest: tidy(s), units: null, pack: "" };
}

// Units from a pack string Brian typed on the sheet: "12 x 500g" → 12, "12" → 12, "" → null.
export function unitsFromPack(text) {
  const t = (text || "").trim();
  if (!t) return null;
  if (/^\d+$/.test(t)) return parseInt(t, 10);
  const m = t.match(/^(\d+)\s*[xX×*]/);
  if (m) return parseInt(m[1], 10);
  return splitPack(t).units;
}

// English / Korean split of ONE segment (prefix and pack already removed).
function splitLang(body) {
  const parens = [];
  // one nesting level allowed, e.g. "(고향만두(소))"
  const outsideRaw = body.replace(/\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g, (_, inner) => { parens.push(inner.trim()); return " "; });
  const out = tidy(outsideRaw);
  const koParens = parens.filter(hasHangul);
  const enParens = parens.filter(t => !hasHangul(t) && hasLatin(t));
  const withParens = (base, list) => (list.length ? `${base} (${list.join(") (")})` : base);
  let en = "";
  let ko = "";
  if (hasHangul(out) && !hasLatin(out)) {
    // "(Solar Seasalt)" is an English name; a lone short word like "(mild)" is a
    // Korean-name qualifier and stays with the Korean so English mode can fall back.
    const realEn = enParens.filter(t => t.split(/\s+/).length >= 2 || t.length >= 6);
    const qualifiers = enParens.filter(t => !realEn.includes(t));
    ko = withParens(out, [...koParens, ...qualifiers]);
    en = realEn.join(" ");
  } else if (hasLatin(out) && !hasHangul(out)) {
    en = withParens(out, enParens);
    ko = koParens.join(" ");
  } else if (out) {
    // mixed outside text, e.g. "대풍 Sweet Rice (대풍찹쌀)"
    en = withParens(out, enParens);
    ko = koParens.join(" ") || out;
  } else {
    en = enParens.join(" ");
    ko = koParens.join(" ");
  }
  return { en: tidy(en), ko: tidy(ko) };
}

// Main entry. Returns { en, ko, pack, units, raw }. en/ko may be "" when the name has only one language.
export function parseProductName(name) {
  const raw = name || "";
  const segments = raw.split(/\.{2,}/).map(s => s.trim()).filter(Boolean);
  let en = "";
  let ko = "";
  let pack = "";
  let units = null;
  for (const seg of segments) {
    const noPrefix = seg.replace(/^[^\s()]{1,8}\)\s*/, ""); // "PMO) ", "HT) ", "동원) "
    const { rest, units: u, pack: pk } = splitPack(noPrefix);
    if (!pack && pk) { pack = pk; units = u; }
    if (units == null && u != null) units = u;
    const lang = splitLang(rest);
    if (!en && lang.en) en = lang.en;
    if (!ko && lang.ko) ko = lang.ko;
  }
  return { en, ko, pack, units, raw };
}

// Name lines for the sheet: "both" = English then Korean (whichever exist),
// "en" / "ko" = that language with the other as fallback so no card ever prints blank.
export function nameLines(parsed, mode) {
  const { en, ko } = parsed;
  if (mode === "en") return [en || ko].filter(Boolean);
  if (mode === "ko") return [ko || en].filter(Boolean);
  return [en, ko].filter(Boolean);
}
